import { i18n_locale as i18nLocale, LONG_UNIT_DISPLAY_ONLY_LOCALES, i18n_t as $t } from '@/utils/i18n';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import {
    DAY,
    HOUR,
    MINUTE,
    SECOND,
    addCommasToNumber as mwAddCommasToNumber,
    formatMeasure as mwFormatMeasure,
    formatNumberAsText as mwFormatNumberAsText,
    formatNumberWithSuffix as mwFormatNumberWithSuffix,
    formatPercentage as mwFormatPercentage,
    formatRevenue as mwFormatRevenue,
    largestRemainderRound as mwLargestRemainderRound,
    pluralize as mwPluralize,
    pluralizeSI as mwPluralizeSI,
    prettyTime as mwPrettyTime,
    prettyTimestamp as mwPrettyTimestamp,
    roundPercentage as mwRoundPercentage,
    simplePadZero as mwSimplePadZero,
    trimmedPrettyTimestamp as mwTrimmedPrettyTimestamp,
} from '@pendo/middleware';
import isNumber from 'lodash/isNumber';
import uniq from 'lodash/uniq';

// Pure helpers live in @pendo/middleware so backends (analytics-microservice,
// MCP, digest renderers, etc.) can share them without depending on Vue. This
// file is a thin main-ui shell that:
//   1. Re-exports the duration / number constants.
//   2. Wraps every locale-aware formatter so callers don't have to thread the
//      active i18n locale through each call site.
//   3. Keeps the host-only helpers (replay timestamps, schema dispatch,
//      i18n-bound URL validation) here where their main-ui deps live.

export { SECOND, MINUTE, HOUR, DAY };

export const pluralize = mwPluralize;

export const formatNumberAsText = mwFormatNumberAsText;

export const roundPercentage = mwRoundPercentage;

export const largestRemainderRound = mwLargestRemainderRound;

export const simplePadZero = mwSimplePadZero;

export const prettyTimestamp = mwPrettyTimestamp;

export const trimmedPrettyTimestamp = mwTrimmedPrettyTimestamp;

export function formatNumberWithSuffix (number, options = {}) {
    return mwFormatNumberWithSuffix(number, { locale: i18nLocale?.value, ...options });
}

export function formatPercentage (number, maxDecimalDigits = 2) {
    return mwFormatPercentage(number, maxDecimalDigits, { locale: i18nLocale?.value });
}

export function formatRevenue (number) {
    return mwFormatRevenue(number, { locale: i18nLocale?.value });
}

export function addCommasToNumber (num) {
    return mwAddCommasToNumber(num, { locale: i18nLocale?.value });
}

export function pluralizeSI (str, count) {
    return mwPluralizeSI(str, count, { locale: i18nLocale?.value });
}

export function prettyTime (input, type = 'short', limitUnits = 2, useI18n = false) {
    return mwPrettyTime(input, type, limitUnits, useI18n, {
        locale: i18nLocale?.value,
        longUnitDisplayLocales: LONG_UNIT_DISPLAY_ONLY_LOCALES,
    });
}

// Canonical measure-value formatter — the single source of truth for
// count / ratio / duration / minutes / percent rendering lives in
// @pendo/middleware (`getMeasureFormatStyle` decides count vs ratio from
// the measure's `reducerKind`). This wrapper only injects the active i18n
// locale. Pass `{ registry }` to format custom measures.
export function formatMeasure (measureKey, value, options = {}) {
    return mwFormatMeasure(measureKey, value, {
        locale: i18nLocale?.value,
        longUnitDisplayLocales: LONG_UNIT_DISPLAY_ONLY_LOCALES,
        ...options,
    });
}

/** Wall-clock time for replay event / dev log rows (agg `browserTime`, ms epoch; subscription TZ). */
export function prettyReplayUserTimeEventLog (browserTimeMs) {
    return moment(browserTimeMs).format(DATE_FORMAT.replayUserTimeEventLog);
}

/** Wall-clock for replay player clock / hints (absolute ms epoch; subscription TZ). */
export function prettyReplayUserTimeFull (wallClockMs) {
    if (wallClockMs == null || !Number.isFinite(wallClockMs)) {
        return '';
    }

    return moment(wallClockMs).format(DATE_FORMAT.replayUserTimeFull);
}

export function formatValueForSchema (value, schema, dateFormat = 'full') {
    if (!schema) {
        return value;
    }

    switch (schema) {
        case 'responseCount':
            if (!isNumber(value)) {
                return value || '';
            }

            return `${value} responses`;
        case 'nps':
            if (!isNumber(value)) {
                return value || '';
            }

            return Math.round(value * 100);
        case 'time':
        case 'date':
            if (!isNumber(value)) {
                return value || '';
            }

            if (value === 0) {
                return '';
            }

            if ([undefined, null, NaN, Infinity, -Infinity].includes(value)) {
                return '--';
            }

            return moment(value).format(DATE_FORMAT[dateFormat]);
        case 'list':
            if (value && value.join) {
                return value.join(', ');
            }

            return value;
        case 'prettyTime':
            if (isNumber(value) && value === 0) {
                return '--';
            }

            return prettyTime(value, 'short', 2, true);
        case 'guideStatus':
            // Remove the "guide" prefix
            return (value || '').replace(/^guide/, '');
        case 'boolean':
            // ! pendo-table's default sorting method will not sort the return value from the method properly!
            // ! use sortBy prop in the column settings to define sorting actions.
            if (value === true || (typeof value === 'string' && value.toLowerCase().trim() === 'true')) {
                return 'True';
            }
            if (value === false || (typeof value === 'string' && value.toLowerCase().trim() === 'false')) {
                return 'False';
            }

            return value;
        case 'yesNo': {
            if (value === true || (typeof value === 'string' && value.toLowerCase().trim() === 'true')) {
                return 'Yes';
            }
            if (value === false || (typeof value === 'string' && value.toLowerCase().trim() === 'false')) {
                return 'No';
            }

            return value;
        }
        case 'number':
            return value || 0;
        case 'percentage':
            if (!isNumber(value)) {
                return 'N/A';
            }

            return `${value}%`;
        case 'duration':
            if (value >= DAY || value >= HOUR) {
                return prettyTime(value, 'short', 2, true);
            }
            if (value >= MINUTE) {
                return prettyTime((value / MINUTE) * MINUTE, 'short', 2, true);
            }
            if (value < MINUTE && value >= SECOND) {
                return `${prettyTime(Math.floor(value / SECOND) * SECOND, 'short', 2, true)}`;
            }
            if (value < SECOND) {
                return `${prettyTime(SECOND, 'short', 2, true)}`;
            }

            return '--';
        case 'guideCount':
        case 'pageCount':
        case 'featureCount':
            return uniq(value).length;
        case 'revenue':
            value = Number(value);
            if (isNaN(value) || !isNumber(value)) return 'N/A';

            return `${formatRevenue(value)}`;
        default:
            if (value === null) {
                return '';
            }

            return value;
    }
}

export function formatRegion (value) {
    // For now this is only used for region groupings in reporting, the value is coming back with the country code first then the region code (ex: US-nc).
    // Eventually we will want to add better region formatting across the app.
    if (value === '(none)' || !value) {
        return '(none)';
    }

    return value.toUpperCase();
}

export function formatCountry (value) {
    if (!value) {
        return value;
    }

    const countryNamesInEnglish = new Intl.DisplayNames(i18nLocale, { type: 'region' });

    return `${value} (${countryNamesInEnglish.of(value)})`;
}

const urlValidationRegex = new RegExp('^(http|https|chrome-extension)://');

export const urlValidationRule = () => [
    {
        required: true,
        message: $t(
            'formatters.Start the URL with http://, https://, or chrome-extension://.',
        ),
        pattern: urlValidationRegex,
        trigger: ['blur', 'change'],
    },
];

export function prettifyPartialJson (input) {
    if (!input.trim().match(/^[{[]/) && !input.includes('":')) {
        return input;
    }

    const IND = '    ';
    let result = '';
    let level = 0;
    let inString = false;
    let escaped = false;

    for (let i = 0; i < input.length; i++) {
        const char = input[i];

        if (inString) {
            result += char;
            if (escaped) {
                escaped = false;
            } else if (char === '\\') {
                escaped = true;
            } else if (char === '"') {
                inString = false;
            }
        } else {
            switch (char) {
                case '"':
                    inString = true;
                    result += '"';
                    break;

                case '{':
                case '[': {
                    // Check if next non-whitespace char is the closing brace
                    const nextChar = input[i + 1];
                    const closingChar = char === '{' ? '}' : ']';

                    if (nextChar === closingChar) {
                        // Empty object/array - keep on same line
                        result += char + closingChar;
                        i++; // Skip the closing brace
                    } else {
                        // Non-empty - add newline and indent
                        result += `${char}\n${IND.repeat(++level)}`;
                    }
                    break;
                }

                case '}':
                case ']':
                    result += `\n${IND.repeat(Math.max(--level, 0))}${char}`;
                    break;

                case ',':
                    result += `,\n${IND.repeat(level)}`;
                    break;

                case ':':
                    result += ': ';
                    break;

                case '\n':
                case '\r':
                    result += `\n${IND.repeat(level)}`;
                    break;

                case ' ':
                    if (inString) {
                        result += char;
                    }
                    break;

                default:
                    result += char;
            }
        }
    }

    return result;
}
